/**
 * LeadsContext - Gestión específica del estado de leads
 * Separado del DataContext para mejor organización y performance
 *
 * Optimización: Usa selectores memoizados para reducir re-renders innecesarios
 *
 * IMPORTANT: This context now uses TenantContext to filter leads
 * by the selected project and company.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Lead } from '@/modules/radar/types';
import { leadsService } from '@/services/api/leadsService';
import { calculateLeadsPositions } from '@/utils/mockData';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';

interface LeadsContextType {
  // Estado
  leads: Lead[];
  leadCount: number;
  isLoading: boolean;
  error: Error | null;

  // Acciones
  setLeads: React.Dispatch<React.SetStateAction<Lead[]>>;
  updateLead: (id: string, updates: Partial<Lead>) => void;
  deleteLead: (id: string) => void;
  addLead: (lead: Lead) => void;
  refreshLeads: () => void;

  // Selectores memoizados (optimización de performance)
  selectors: {
    getLeadById: (id: string) => Lead | undefined;
    getLeadsByStage: (stage: number) => Lead[];
    getHighValueLeads: (minValue?: number) => Lead[];
    getHighScoreLeads: (minScore?: number) => Lead[];
    getRecentLeads: (days?: number) => Lead[];
    getLeadsByStatus: (status: string) => Lead[];
    getLeadsStats: () => {
      totalValue: number;
      avgScore: number;
      conversionRate: number;
      highValueCount: number;
      totalCount: number;
    };
  };
}

const LeadsContext = createContext<LeadsContextType | undefined>(undefined);

export function LeadsProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { selectedProject, effectiveCompanyId, isPlatform } = useTenant();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadCount, setLeadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Build query params for filtering by project/company
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page_size: 100 // Reducir a 100 para mejorar performance inicial
    };

    // Add project filter if selected
    if (selectedProject?.id) {
      params.project_id = selectedProject.id;
    }

    // Add company filter if no project selected (for platform users viewing all)
    if (!selectedProject?.id && effectiveCompanyId) {
      params.company_id = effectiveCompanyId;
    }

    return params;
  }, [selectedProject?.id, effectiveCompanyId]);

  // Cargar leads desde el backend
  const loadLeadsFromBackend = useCallback(async () => {
    // Don't load if auth is still loading or no user
    if (authLoading || !user) {
      setIsLoading(false);
      return;
    }

    // Only load if we have a project selected, company filter, or user is platform
    // Tenant users can load by their company even without a project selected
    if (!selectedProject?.id && !effectiveCompanyId && !isPlatform) {
      setIsLoading(false);
      setLeads([]);
      setLeadCount(0);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await leadsService.getLeads(queryParams);

      // Note: leadsService.getLeads() already transforms data using adaptAPILeadsToFrontend()
      // No need to call adapter again here - response.results already contains Lead[] objects
      const transformedLeads: Lead[] = response.results;

      // Calculate positions for radar visualization
      const leadsWithPositions = calculateLeadsPositions(transformedLeads);
      setLeads(leadsWithPositions);
      setLeadCount(response.count);
    } catch (err) {
      setError(err as Error);
      // No fallback - mantener array vacío si hay error
      setLeads([]);
      setLeadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [authLoading, user, selectedProject?.id, effectiveCompanyId, isPlatform, queryParams]);

  // Refrescar leads (útil para re-inicializar)
  const refreshLeads = useCallback(() => {
    loadLeadsFromBackend();
  }, [loadLeadsFromBackend]);

  // NO cargar automáticamente - solo cuando se llame a refreshLeads()
  // Esto evita cargas innecesarias en páginas que no usan leads (como /projects)
  // Las páginas que necesiten leads deben llamar a refreshLeads() manualmente
  useEffect(() => {
    // Inicializar en estado "no loading" sin cargar datos
    if (!authLoading) {
      setIsLoading(false);
    }
  }, [authLoading]);

  // Actualizar un lead
  const updateLead = useCallback((id: string, updates: Partial<Lead>) => {
    setLeads(prevLeads =>
      prevLeads.map(lead =>
        lead.id === id ? { ...lead, ...updates } : lead
      )
    );
  }, []);

  // Eliminar un lead
  const deleteLead = useCallback((id: string) => {
    setLeads(prevLeads => prevLeads.filter(lead => lead.id !== id));
    setLeadCount(prev => prev - 1);
  }, []);

  // Agregar un lead
  const addLead = useCallback((lead: Lead) => {
    setLeads(prevLeads => [...prevLeads, lead]);
    setLeadCount(prev => prev + 1);
  }, []);

  // ========================================
  // SELECTORES MEMOIZADOS (Optimización de performance)
  // ========================================
  const selectors = useMemo(() => ({
    // Buscar lead por ID
    getLeadById: (id: string) => {
      return leads.find(lead => lead.id === id);
    },

    // Filtrar leads por stage
    getLeadsByStage: (stage: number) => {
      return leads.filter(lead => lead.stage === stage);
    },

    // Obtener leads de alto valor
    getHighValueLeads: (minValue = 50000) => {
      return leads.filter(lead => lead.value >= minValue);
    },

    // Obtener leads de alto score
    getHighScoreLeads: (minScore = 70) => {
      return leads.filter(lead => (lead.score || 0) >= minScore);
    },

    // Obtener leads recientes (por último contacto)
    getRecentLeads: (days = 7) => {
      const cutoffDate = Date.now() - (days * 24 * 60 * 60 * 1000);
      return leads.filter(lead => {
        const lastContact = new Date(lead.lastContact || '2024-01-01').getTime();
        return lastContact >= cutoffDate;
      });
    },

    // Filtrar por status
    getLeadsByStatus: (status: string) => {
      return leads.filter(lead => lead.stage === parseInt(status));
    },

    // Calcular estadísticas generales (memoizado)
    getLeadsStats: () => {
      const totalValue = leads.reduce((sum, lead) => sum + lead.value, 0);
      const avgScore = leads.length > 0
        ? leads.reduce((sum, lead) => sum + (lead.score || 0), 0) / leads.length
        : 0;
      const qualifiedLeads = leads.filter(lead => lead.stage === 0).length;
      const conversionRate = leads.length > 0 ? (qualifiedLeads / leads.length) * 100 : 0;
      const highValueCount = leads.filter(lead => lead.value >= 50000).length;

      return {
        totalValue,
        avgScore,
        conversionRate,
        highValueCount,
        totalCount: leads.length
      };
    }
  }), [leads]); // Solo recalcular cuando cambien los leads

  const value: LeadsContextType = {
    leads,
    leadCount,
    isLoading,
    error,
    setLeads,
    updateLead,
    deleteLead,
    addLead,
    refreshLeads,
    selectors,
  };

  return <LeadsContext.Provider value={value}>{children}</LeadsContext.Provider>;
}

export function useLeadsContext() {
  const context = useContext(LeadsContext);
  if (context === undefined) {
    throw new Error('useLeadsContext must be used within a LeadsProvider');
  }
  return context;
}

// ========================================
// CUSTOM HOOKS CON SELECTORES (Optimización)
// ========================================

/**
 * Hook para obtener un lead específico por ID
 * Evita re-renders innecesarios al solo devolver el lead específico
 */
export function useLeadById(id: string | null | undefined) {
  const { selectors } = useLeadsContext();
  return useMemo(() => {
    if (!id) return undefined;
    return selectors.getLeadById(id);
  }, [id, selectors]);
}

/**
 * Hook para obtener estadísticas de leads memoizadas
 * Útil para dashboards y componentes de resumen
 */
export function useLeadsStats() {
  const { selectors, isLoading } = useLeadsContext();
  return useMemo(() => ({
    ...selectors.getLeadsStats(),
    isLoading
  }), [selectors, isLoading]);
}

/**
 * Hook para obtener leads de alto valor
 * Útil para componentes de leads premium
 */
export function useHighValueLeads(minValue = 50000) {
  const { selectors, isLoading } = useLeadsContext();
  return useMemo(() => ({
    leads: selectors.getHighValueLeads(minValue),
    isLoading
  }), [selectors, minValue, isLoading]);
}

/**
 * Hook para obtener leads de alto score
 * Útil para componentes de leads prioritarios/hot leads
 */
export function useHighScoreLeads(minScore = 70) {
  const { selectors, isLoading } = useLeadsContext();
  return useMemo(() => ({
    leads: selectors.getHighScoreLeads(minScore),
    isLoading
  }), [selectors, minScore, isLoading]);
}

/**
 * Hook para obtener leads recientes
 * Útil para componentes de actividad reciente
 */
export function useRecentLeads(days = 7) {
  const { selectors, isLoading } = useLeadsContext();
  return useMemo(() => ({
    leads: selectors.getRecentLeads(days),
    isLoading
  }), [selectors, days, isLoading]);
}

/**
 * Hook para obtener leads por stage
 * Útil para componentes de pipeline
 */
export function useLeadsByStage(stage: number) {
  const { selectors, isLoading } = useLeadsContext();
  return useMemo(() => ({
    leads: selectors.getLeadsByStage(stage),
    isLoading
  }), [selectors, stage, isLoading]);
}
